import { useState } from 'react';
import {
  X,
  Gavel,
  FileText,
  Building2,
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  Printer,
  Download,
  Play,
  Pause,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import { Button } from '@/components/ui/button';
import type { SourcingEvent } from '../../types';
import {
  SOURCING_STATUS_LABELS,
  SOURCING_STATUS_COLORS,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_COLORS,
} from '../../types';
import type {
  TabValue, Tab 
} from './types';
import { DetailsTab } from './DetailsTab';
import { ItemsTab } from './ItemsTab';
import { BidsTab } from './BidsTab';
import { TimelineTab } from './TimelineTab';

interface SourcingEventDetailModalProps {
  readonly event: SourcingEvent;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onStartAuction?: (eventId: string) => void;
  readonly onEndAuction?: (eventId: string) => void;
  readonly onAwardBid?: (eventId: string) => void;
  readonly onCancel?: (eventId: string) => void;
}

const TABS: Tab[] = [
  {
    value: 'details',
    label: 'Details',
    icon: <FileText className="h-4 w-4" />,
  },
  {
    value: 'items',
    label: 'Line Items',
    icon: <Package className="h-4 w-4" />,
  },
  {
    value: 'bids',
    label: 'Bids',
    icon: <Gavel className="h-4 w-4" />,
  },
  {
    value: 'timeline',
    label: 'Timeline',
    icon: <Clock className="h-4 w-4" />,
  },
];

export function SourcingEventDetailModal({
  event,
  isOpen,
  onClose,
  onStartAuction,
  onEndAuction,
  onAwardBid,
  onCancel,
}: SourcingEventDetailModalProps) {
  const [activeTab, setActiveTab] = useState<TabValue>('details');

  if (!isOpen) return null;

  const isReverseAuction = event.eventType === 'reverse_auction';
  const canStart = event.status === 'scheduled' && isReverseAuction;
  const canEnd = event.status === 'open' && isReverseAuction;
  const canAward = event.status === 'evaluation';
  const canCancel = ['draft', 'scheduled', 'open'].includes(event.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-50 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <ModalHeader event={event} onClose={onClose} />
        <TabNavigation tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && <DetailsTab event={event} />}
          {activeTab === 'items' && <ItemsTab event={event} />}
          {activeTab === 'bids' && <BidsTab event={event} />}
          {activeTab === 'timeline' && <TimelineTab event={event} />}
        </div>
        <ModalFooter
          event={event}
          canStart={canStart}
          canEnd={canEnd}
          canAward={canAward}
          canCancel={canCancel}
          onStartAuction={onStartAuction}
          onEndAuction={onEndAuction}
          onAwardBid={onAwardBid}
          onCancel={onCancel}
        />
      </div>
    </div>
  );
}

function ModalHeader({
  event,
  onClose,
}: {
  readonly event: SourcingEvent;
  readonly onClose: () => void;
}) {
  const isReverseAuction = event.eventType === 'reverse_auction';

  return (
    <div className="flex items-center justify-between border-b px-6 py-4">
      <div className="flex items-center gap-4">
        <div className={cn('rounded-lg p-3', isReverseAuction ? 'bg-orange-100' : 'bg-indigo-100')}>
          {isReverseAuction ? (
            <Gavel className="h-6 w-6 text-orange-600" />
          ) : (
            <FileText className="h-6 w-6 text-indigo-600" />
          )}
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{event.title}</h2>
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Building2 className="h-4 w-4" />
            {event.eventNumber}
          </p>
        </div>
        <span
          className={cn(
            'rounded-full px-3 py-1 text-sm font-medium',
            EVENT_TYPE_COLORS[event.eventType]
          )}
        >
          {EVENT_TYPE_LABELS[event.eventType]}
        </span>
        <span
          className={cn(
            'rounded-full px-3 py-1 text-sm font-medium',
            SOURCING_STATUS_COLORS[event.status]
          )}
        >
          {SOURCING_STATUS_LABELS[event.status]}
        </span>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}

function TabNavigation({
  tabs,
  activeTab,
  onTabChange,
}: {
  readonly tabs: Tab[];
  readonly activeTab: TabValue;
  readonly onTabChange: (tab: TabValue) => void;
}) {
  return (
    <div className="flex gap-1 border-b px-6">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          onClick={() => onTabChange(tab.value)}
          className={cn(
            'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors',
            activeTab === tab.value
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
          )}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

interface ModalFooterProps {
  readonly event: SourcingEvent;
  readonly canStart: boolean;
  readonly canEnd: boolean;
  readonly canAward: boolean;
  readonly canCancel: boolean;
  readonly onStartAuction?: (eventId: string) => void;
  readonly onEndAuction?: (eventId: string) => void;
  readonly onAwardBid?: (eventId: string) => void;
  readonly onCancel?: (eventId: string) => void;
}

function ModalFooter({
  event,
  canStart,
  canEnd,
  canAward,
  canCancel,
  onStartAuction,
  onEndAuction,
  onAwardBid,
  onCancel,
}: ModalFooterProps) {
  return (
    <div className="flex items-center justify-between border-t px-6 py-4">
      <div className="flex gap-2">
        <Button variant="outline" size="sm">
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
        <Button variant="outline" size="sm">
          <Download className="mr-2 h-4 w-4" />
          Export PDF
        </Button>
      </div>
      <div className="flex gap-2">
        {canCancel && onCancel && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700"
            onClick={() => onCancel(event.eventId)}
          >
            <XCircle className="mr-2 h-4 w-4" />
            Cancel Event
          </Button>
        )}
        {canStart && onStartAuction && (
          <Button size="sm" onClick={() => onStartAuction(event.eventId)}>
            <Play className="mr-2 h-4 w-4" />
            Start Auction
          </Button>
        )}
        {canEnd && onEndAuction && (
          <Button size="sm" onClick={() => onEndAuction(event.eventId)}>
            <Pause className="mr-2 h-4 w-4" />
            End Auction
          </Button>
        )}
        {canAward && onAwardBid && (
          <Button size="sm" onClick={() => onAwardBid(event.eventId)}>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Award Bid
          </Button>
        )}
      </div>
    </div>
  );
}
